import { useId, useState } from 'react';
import { X } from 'lucide-react';
import { useUiText } from '../lib/ui-text';
import { Button } from './button';
import { Input } from './input';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * A list of additional email addresses. Typed addresses join the list on
 * Enter, on Add, or when the input loses focus, so a form submit never drops
 * an address that was typed but not added.
 */
export function EmailListField({
  label,
  hint,
  value,
  onChange,
  signInEmail,
  max,
  disabled = false,
}: {
  label: string;
  hint?: string;
  value: string[];
  onChange: (emails: string[]) => void;
  /** Rejected as an additional address because it already counts. */
  signInEmail?: string;
  max: number;
  disabled?: boolean;
}) {
  const uiText = useUiText();
  const id = useId();
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const full = value.length >= max;

  const add = () => {
    const email = input.trim().toLowerCase();
    if (!email) return true;
    if (!EMAIL.test(email)) {
      setError(uiText('invalidAdditionalEmail', { email }));
      return false;
    }
    if (email === signInEmail?.trim().toLowerCase()) {
      setError(uiText('additionalEmailIsSignIn'));
      return false;
    }
    if (value.some(current => current.toLowerCase() === email)) {
      setInput('');
      setError(null);
      return true;
    }
    if (full) {
      setError(uiText('tooManyAdditionalEmails', { count: max }));
      return false;
    }
    onChange([...value, email]);
    setInput('');
    setError(null);
    return true;
  };

  return (
    <div className="space-y-2" role="group" aria-labelledby={`${id}-label`}>
      <span id={`${id}-label`} className="block text-sm font-medium">
        {label}
      </span>
      {value.length > 0 && (
        <ul aria-label={label} className="flex flex-wrap gap-2">
          {value.map(email => (
            <li
              key={email}
              className="flex max-w-full items-center gap-1 rounded-3xl border bg-muted px-3 py-1 text-sm"
            >
              <span className="break-all">{email}</span>
              <button
                type="button"
                aria-label={uiText('removeEmail', { email })}
                disabled={disabled}
                className="shrink-0 rounded-3xl p-1 text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:opacity-50"
                onClick={() => onChange(value.filter(current => current !== email))}
              >
                <X aria-hidden="true" className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {(!full || input) && (
        <div className="flex max-w-xl flex-wrap gap-2">
          <Input
            id={id}
            type="email"
            aria-label={uiText('addAdditionalEmail')}
            className="min-w-0 flex-1 basis-48"
            placeholder="name@hospital.example"
            autoCapitalize="none"
            autoComplete="off"
            spellCheck={false}
            value={input}
            disabled={disabled}
            aria-invalid={!!error}
            onChange={event => {
              setInput(event.target.value);
              setError(null);
            }}
            onBlur={() => void add()}
            onKeyDown={event => {
              if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
                event.preventDefault();
                add();
              }
            }}
          />
          <Button
            type="button"
            variant="outline"
            disabled={disabled || !input.trim()}
            onClick={() => void add()}
          >
            {uiText('addEmail')}
          </Button>
        </div>
      )}
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
